import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { api, graphProductToCard, imgUrl } from "@/lib/api";
import { relatedSearches, SEARCH_PLACEHOLDERS } from "@/constants/search";

export default function SearchBox({
  value,
  onChange,
  onSearch,
  testId = "search",
  className = "",
}) {
  const [hint, setHint] = useState(0);
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    if (value) return undefined;
    const id = setInterval(() => setHint((i) => (i + 1) % SEARCH_PLACEHOLDERS.length), 3200);
    return () => clearInterval(id);
  }, [value]);

  useEffect(() => {
    const q = String(value || "").trim();
    if (q.length < 2) {
      setHits([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/graph/search", { query: q, filters: { suggest: true }, limit: 6 });
        setHits((data?.products || []).map(graphProductToCard).filter((p) => p.id).slice(0, 5));
      } catch {
        setHits([]);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const related = relatedSearches(value);
  const showPanel = open && (related.length > 0 || hits.length > 0);

  const go = (q) => {
    const next = String(q || "").trim();
    if (next && next !== value) onChange(next);
    setOpen(false);
    onSearch(next);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          go(value);
        }}
      >
        <label className="flex items-center gap-2.5 w-full h-11 px-4 rounded-full border border-aura-border bg-aura-surface focus-within:border-aura-accent">
          <Search className="w-4 h-4 text-aura-muted shrink-0" />
          <span className="relative flex-1 min-w-0 h-5">
            {!value && (
              <span className="absolute inset-0 text-sm text-aura-muted truncate pointer-events-none" aria-hidden>
                {SEARCH_PLACEHOLDERS[hint]}
              </span>
            )}
            <input
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              className="absolute inset-0 w-full bg-transparent outline-none text-sm"
              data-testid={testId}
              aria-label="Search Shoptosa"
            />
          </span>
        </label>
      </form>
      {showPanel && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-[60] border border-aura-border bg-aura-bg shadow-2xl max-h-[70vh] overflow-y-auto"
          data-testid="search-suggest"
        >
          <div className="px-4 pt-3 pb-2 text-[10px] tracking-luxe uppercase text-aura-muted">Related searches</div>
          <div className="px-3 pb-3 flex flex-wrap gap-2">
            {related.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => go(s)}
                className="text-xs px-3 py-1.5 border border-aura-border text-aura-ink/80 hover:border-aura-accent hover:text-aura-accent"
                data-testid={`suggest-${s.replace(/\s+/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
          {hits.length > 0 && (
            <>
              <div className="px-4 pt-1 pb-2 text-[10px] tracking-luxe uppercase text-aura-muted border-t border-aura-border">Matching items</div>
              {hits.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => go(p.title)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-aura-surface"
                >
                  <span className="w-10 h-10 shrink-0 bg-aura-surface overflow-hidden">
                    {p.images?.[0] ? (
                      <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm truncate">{p.title}</span>
                    <span className="block text-[11px] text-aura-muted truncate">{p.store || p.seller_name}</span>
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
